import { fabric } from "fabric"
import GroupObject from "../objects/group"
import Calc from "@/common/util/calculator"
import BaseFixmethod from "@/signomatic/app/fixmethods/base"
import CircleObject from "@/signomatic/app/objects/circle"
import RectObject from "@/signomatic/app/objects/rect"

export default class PoleFixmethod extends BaseFixmethod {
  get visualPadding() {
    return { top: Calc.mmToPx(12), bottom: Calc.mmToPx(12), left: 0, right: 0 }
  }

  get externalTop(): boolean {
    return this.state.fixmethod === "pole_flanged"
  }

  get externalBottom(): boolean {
    return this.state.fixmethod === "pole_flanged"
  }

  get externalSides(): boolean {
    return this.state.fixmethod === "pole_flanged" && this.state.height >= 300
  }

  get externalGap(): number {
    return 52
  }

  topDrawable() {
    if (this.state.shape === "triangle") {
      return null
    }

    const items: fabric.Object[] = []

    for (const e of this.positions()) {
      this.drawHole(items, e.left, e.top)
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: this.state.widthPx,
        height: this.state.heightPx,
      }),
    ]
  }

  bottomDrawable() {
    const items = []
    const poleWidth = Calc.mmToPx(60)
    const twoPoles = this.state.width >= 1000

    let left = twoPoles ? 0 - this.state.widthPx / 2.5 : 0 - poleWidth / 2

    if (["direction_left", "direction_right"].includes(this.state.shape)) {
      const fromCenter = this.state.heightPx / 4

      left = this.state.shape == "direction_left" ? fromCenter : -fromCenter

      const stretchPole = new RectObject({
        fill: "transparent",
        width: poleWidth,
        height: this.state.heightPx * 2,
        left: -left,
      }).addClass("stretch")

      items.push(stretchPole)
    }

    const pole = new RectObject({
      fill: "black",
      width: poleWidth,
      height: this.state.heightPx * 2,
      left: left,
    }).addClass("preview")

    pole.set(
      "fill",
      new fabric.Gradient({
        coords: {
          x1: 0,
          y1: 0,
          x2: pole.width,
          y2: 0,
        },
        colorStops: [
          { offset: 0, color: "#8a8a8a" },
          { offset: 0.2, color: "#b8b7b7" },
          { offset: 0.8, color: "#b8b7b7" },
          { offset: 1, color: "#8a8a8a" },
        ],
      })
    )

    items.push(pole)

    if (twoPoles) {
      const pole2 = new RectObject({
        fill: "black",
        width: poleWidth,
        height: this.state.heightPx * 2,
        left: 0 + this.state.width / 2,
      }).addClass("preview")

      pole2.set(
        "fill",
        new fabric.Gradient({
          coords: {
            x1: 0,
            y1: 0,
            x2: pole.width,
            y2: 0,
          },
          colorStops: [
            { offset: 0, color: "#8a8a8a" },
            { offset: 0.2, color: "#b8b7b7" },
            { offset: 0.8, color: "#b8b7b7" },
            { offset: 1, color: "#8a8a8a" },
          ],
        })
      )

      items.push(pole2)
    }

    return new GroupObject(items, {
      left: 0,
      top: 0,
      width: this.state.widthPx,
      height: this.state.heightPx,
    })
  }

  private drawHole(items: fabric.Object[], left: number, top: number) {
    const diameter = Calc.mmToPx(4)
    const head = Calc.mmToPx(8)
    const radius = head / 2
    const holeRadius = diameter / 2

    items.push(
      new CircleObject({
        left,
        top,
        radius: holeRadius,
        stroke: "none",
        strokeWidth: 0,
        startAngle: 0,
        endAngle: Calc.toDeg(Math.PI * 2),
        fill: "transparent",
        originX: "center",
        originY: "center",
      }).addClass("hang", "pole")
    )

    items.push(
      new CircleObject({
        left,
        top,
        radius,
        stroke: "none",
        strokeWidth: 0,
        startAngle: 0,
        endAngle: Calc.toDeg(Math.PI * 2),
        fill: "#EAEAEA",
        originX: "center",
        originY: "center",
      })
    )

    items.push(
      new CircleObject({
        left,
        top,
        radius: radius / 3,
        stroke: "none",
        strokeWidth: 0,
        startAngle: 0,
        endAngle: Calc.toDeg(Math.PI * 2),
        fill: "#A7A7A7",
        originX: "center",
        originY: "center",
      })
    )

    items.push(
      new CircleObject({
        left,
        top,
        radius: radius / 3 / 1.2,
        stroke: "none",
        strokeWidth: 0,
        startAngle: 0,
        endAngle: Calc.toDeg(Math.PI * 2),
        fill: "#353534",
        originX: "center",
        originY: "center",
      })
    )

    items.push(
      new CircleObject({
        left,
        top,
        radius: radius / 3 / 1.6,
        stroke: "none",
        strokeWidth: 0,
        startAngle: 0,
        endAngle: Calc.toDeg(Math.PI * 2),
        fill: "#656464",
        originX: "center",
        originY: "center",
      })
    )
  }

  private positions() {
    const { width, widthPx, height, heightPx, shape, fixmethod } = this.state
    const pos = []
    const edgeDistance = Calc.mmToPx(28.3)
    let distance

    if (width < 660 || fixmethod === "pole_flanged") {
      return []
    }

    if (width < 760) {
      distance = 626
    } else if (width < 860) {
      distance = 726
    } else if (width < 960) {
      distance = 826
    } else if (width < 1110) {
      distance = 926
    } else if (width < 1260) {
      distance = 1076
    } else if (width < 1410) {
      distance = 1226
    } else if (width < 1560) {
      distance = 1376
    } else if (width < 1710) {
      distance = 1526
    } else if (width < 1860) {
      distance = 1676
    } else {
      distance = 1826
    }

    if (shape === "round") {
      if (width < 760) {
        distance = 626
      } else if (width < 860) {
        distance = 726
      } else if (width < 960) {
        distance = 826
      } else if (width < 1130) {
        distance = 926
      } else if (width < 1300) {
        distance = 1076
      } else if (width < 1430) {
        distance = 1226
      } else if (width < 1615) {
        distance = 1376
      } else if (width < 1760) {
        distance = 1526
      } else if (width < 1910) {
        distance = 1676
      } else {
        distance = 1826
      }
    }

    distance = Calc.mmToPx(distance)

    const centerX = widthPx / 2

    if (height < 250) {
      const centerY = heightPx / 2

      pos.push({ left: centerX - distance / 2, top: centerY })
      pos.push({ left: centerX + distance / 2, top: centerY })
    } else {
      const top = edgeDistance
      const bottom = heightPx - edgeDistance

      pos.push({ left: centerX - distance / 2, top })
      pos.push({ left: centerX + distance / 2, top })
      pos.push({ left: centerX - distance / 2, top: bottom })
      pos.push({ left: centerX + distance / 2, top: bottom })
    }

    return pos
  }
}
